import { useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { parse } from "query-string";

import {
  BasicImgWrapper, COLORS, BasicText, BasicButton, FontFamilyTypeEnum,
  GradientText, SeparatorLine, GRADIENTS,
} from "../../components/styled";
import {
  WelcomePageImageBackground, WelcomePageWrapper, WelcomePageButton, WelcomePageContent,
  WelcomePageAdvPart, WelcomePageImagePart, WelcomePageLogoPart, WelcomePageOptOutButton, WelcomePageTextPart,
} from "./styled";
import { Routes } from "../../utils/routes";

import WelcomeTopImage from "../../assets/images/welcome/WelcomeMiddleImage.png";
import { MomentServiceInstance } from "../../services/moment";
import { StorageServiceContext, StorageServiceInstance } from "../../services/storage";
import { UniqueUser } from "../../shared/interfaces";
import ResumeSessionModal from "../../components/ResumeSessionModal";
import { loadLastSession } from "../../utils/session";

const CLIENT_VERSION = (window as any).appVersion.clientVersion;
const CLIENT_GIT = (window as any).appVersion.clientGit;
const VERSION_STRING = `Client v${CLIENT_VERSION} (${CLIENT_GIT})`;

const WelcomePage = (): JSX.Element => {
  const { search } = useLocation();
  const [showResumeSessionDialog, setShowResumeSessionDialog] = useState(false);
  const [previousSession, setPreviousSession] = useState<UniqueUser>(null);
  const [backgroundUrl, setBackgroundUrl] = useState("");
  const [welcomeMiddleImageUrl, setWelcomeMiddleImageUrl] = useState("https://s3.us-west-2.amazonaws.com/com.momento.prod.images/WelcomePageBackgrounds/MomentoLogo.png");
  const [photosTextColor, setPhotosTextColor] = useState(GRADIENTS.WELCOME_TEXT);
  const [buttonBackgroundColor, setButtonBackgroundColor] = useState(GRADIENTS.NEXT_BUTTON);
  const [buttonTextColor, setButtonTextColor] = useState(COLORS.WHITE); // Default to white
  const [sponsorImageUrl, setSponsorImageUrl] = useState(""); // Default to empty

  const { selectedEmail, resumeSessionDialogWasShown } = useContext(StorageServiceContext);

  const loadPreviousSession = async (email) => {
    setShowResumeSessionDialog(false);
    const lastSession = await loadLastSession(email);
    if (lastSession) {
      setPreviousSession(lastSession);
      setShowResumeSessionDialog(true);
    }
  };

  useEffect(() => {
    if (!search) return;

    const parsed = parse(search);
    const key = parsed.gif as string;
    if (key === "true") {
      MomentServiceInstance.setPregenerateGIFAppType(true);
    }
  }, [search]);

  useEffect(() => {
    const getBackgroundUrlByHost = () => {
      const { hostname } = window.location;
      const hostnamePrefix = hostname?.substring(0, hostname.indexOf(".")).toLowerCase();

      switch (hostnamePrefix) {
        case "kraken":
          return "https://s3.us-west-2.amazonaws.com/com.momento.prod.images/WelcomePageBackgrounds/wp8242304.jpg";
        case "redbulls":
          return "https://s3.us-west-2.amazonaws.com/com.momento.prod.images/WelcomePageBackgrounds/redbullbackground.png";
        case "gothamfc":
          return "https://s3.us-west-2.amazonaws.com/com.momento.prod.images/WelcomePageBackgrounds/redbullbackground.png";
        case "storm":
          return "https://s3.us-west-2.amazonaws.com/com.momento.prod.images/WelcomePageBackgrounds/StormBackground.jpg";
        case "lightning":
          return "https://s3.us-west-2.amazonaws.com/com.momento.prod.images/WelcomePageBackgrounds/TampaBayLightning.jpg";
        case "mariners":
          return "https://s3.us-west-2.amazonaws.com/com.momento.prod.images/WelcomePageBackgrounds/MarinersBackground.jpg";
        case "seahawks":
          return "https://s3.us-west-2.amazonaws.com/com.momento.prod.images/WelcomePageBackgrounds/SeahawksBackground.jpg";
        case "amex":
          return "https://s3.us-west-2.amazonaws.com/com.momento.prod.images/WelcomePageBackgrounds/AmexBackground.jpg";
        default:
          return "https://s3.us-west-2.amazonaws.com/com.momento.prod.images/WelcomePageBackgrounds/DefualtBackground.jpg";
      }
    };

    const getMiddleImageByHost = () => {
      const { hostname } = window.location;
      const hostnamePrefix = hostname?.substring(0, hostname.indexOf(".")).toLowerCase();

      switch (hostnamePrefix) {
        case "kraken":
          return "https://s3.us-west-2.amazonaws.com/com.momento.prod.images/WelcomePageBackgrounds/KrakenTickets.png";
        case "redbulls":
          return "https://s3.us-west-2.amazonaws.com/com.momento.prod.images/WelcomePageBackgrounds/RedbullsLogo.png";
        case "gothamfc":
          return "https://s3.us-west-2.amazonaws.com/com.momento.prod.images/WelcomePageBackgrounds/GothamFCLogo.png";
        case "lightning":
          return "https://s3.us-west-2.amazonaws.com/com.momento.prod.images/WelcomePageBackgrounds/LightningTickets.png";
        case "storm":
          return "https://s3.us-west-2.amazonaws.com/com.momento.prod.images/WelcomePageBackgrounds/StormTickets.png";
        case "mariners":
          return "https://s3.us-west-2.amazonaws.com/com.momento.prod.images/WelcomePageBackgrounds/MarinersLogo.png";
        case "seahawks":
          return "https://s3.us-west-2.amazonaws.com/com.momento.prod.images/WelcomePageBackgrounds/SeahawksLogo.png";
        case "amex":
          return "https://s3.us-west-2.amazonaws.com/com.momento.prod.images/WelcomePageBackgrounds/AmexLogo.png";
        default:
          return "https://s3.us-west-2.amazonaws.com/com.momento.prod.images/WelcomePageBackgrounds/MomentoLogo.png";
      }
    };

    const getPhotosTextColorByHost = () => {
      const { hostname } = window.location;
      const hostnamePrefix = hostname?.substring(0, hostname.indexOf(".")).toLowerCase();

      switch (hostnamePrefix) {
        case "storm":
          return "#FDE81F";
        case "redbulls":
          return "#ED1E36";
        case "gothamfc":
          return "#A9F1FD";
        default:
          return GRADIENTS.WELCOME_TEXT;
      }
    };

    const getButtonBackgroundColorByHost = () => {
      const { hostname } = window.location;
      const hostnamePrefix = hostname?.substring(0, hostname.indexOf(".")).toLowerCase();

      switch (hostnamePrefix) {
        case "storm":
          return "#2C5234";
        case "redbulls":
          return "#ED1E36";
        case "gothamfc":
          return "#A9F1FD";
        case "mariners":
          return "#005C5C";
        case "seahawks":
          return "#00ff01";
        case "amex":
          return "#016FD0";
        default:
          return "#007DB7";
      }
    };

    const getButtonTextColorByHost = () => {
      const { hostname } = window.location;
      const hostnamePrefix = hostname?.substring(0, hostname.indexOf(".")).toLowerCase();

      return hostnamePrefix === "gothamfc" ? "#000000" : COLORS.WHITE;
    };

    const getSponsorImageUrlByHost = () => {
      const { hostname } = window.location;
      const hostnamePrefix = hostname?.substring(0, hostname.indexOf(".")).toLowerCase();

      switch (hostnamePrefix) {
        case "storm":
          return "https://s3.us-west-2.amazonaws.com/com.momento.prod.images/WelcomePageBackgrounds/StormSponsor.png";
        case "mariners":
          return "https://s3.us-west-2.amazonaws.com/com.momento.prod.images/WelcomePageBackgrounds/MarinersBackground.png";
        case "seahawks":
          return "https://s3.us-west-2.amazonaws.com/com.momento.prod.images/WelcomePageBackgrounds/SeahawksSponsor.png";
        case "amex":
          return "https://s3.us-west-2.amazonaws.com/com.momento.prod.images/WelcomePageBackgrounds/AmexSponsor.png";
        case "lightning":
          return "https://s3.us-west-2.amazonaws.com/com.momento.prod.images/WelcomePageBackgrounds/LightningSponsorImage.png";
        case "redbulls":
          return "https://s3.us-west-2.amazonaws.com/com.momento.prod.images/WelcomePageBackgrounds/RedbullsSponsorImage.png";
        case "gothamfc":
          return "https://s3.us-west-2.amazonaws.com/com.momento.prod.images/WelcomePageBackgrounds/RedbullsSponsorImage.png";
        default:
          return "https://s3.us-west-2.amazonaws.com/com.momento.prod.images/WelcomePageBackgrounds/DefaultSponsor.png";
      }
    };

    setBackgroundUrl(getBackgroundUrlByHost());
    setWelcomeMiddleImageUrl(getMiddleImageByHost());
    setPhotosTextColor(getPhotosTextColorByHost());
    setButtonBackgroundColor(getButtonBackgroundColorByHost());
    setButtonTextColor(getButtonTextColorByHost()); // Set button text color

    const sponsorImage = getSponsorImageUrlByHost();
    if (sponsorImage) {
      setSponsorImageUrl(sponsorImage); // Only set if non-empty
    }

    if (!resumeSessionDialogWasShown) {
      loadPreviousSession(selectedEmail);
    }
  }, [selectedEmail, resumeSessionDialogWasShown]);

  const handleNextPage = () => {
    MomentServiceInstance.moveToTheNextPage(Routes.WELCOME);
  };

  const handleSelectNewSeat = () => {
    StorageServiceInstance.setResumeSessionDialogWasShown(true);
    MomentServiceInstance.moveToTheNextPage(Routes.WELCOME);
  };

  const handleResumePreviousSession = () => {
    StorageServiceInstance.setResumeSessionDialogWasShown(true);
    MomentServiceInstance.moveToThePreviousSession(previousSession);
  };

  const { hostname } = window.location;
  const hostnamePrefix = hostname?.substring(0, hostname.indexOf(".")).toLowerCase();

  return (
    <WelcomePageImageBackground url={backgroundUrl}>
      <WelcomePageWrapper>
        <WelcomePageContent>
          <WelcomePageLogoPart>
            <BasicImgWrapper src={WelcomeTopImage} width="80%" height="80%" />
          </WelcomePageLogoPart>
          <WelcomePageImagePart style={{ marginBottom: "30px" }}>
            <BasicImgWrapper src={welcomeMiddleImageUrl} width="370%" height="370%" />
          </WelcomePageImagePart>

          {/* Only hide WelcomePageTextPart for Seahawks */}
          {hostnamePrefix !== "seahawks" && (
            <WelcomePageTextPart>
              <BasicText
                style={{ marginTop: "30px" }}
                fontSize="17px"
                fontFamily={FontFamilyTypeEnum.POPPINS}
                fontWeight="600"
                color={COLORS.WHITE}
                lineHeight="17px"
              >
                Unforgettable Reaction
              </BasicText>
              <GradientText
                fontSize="42px"
                fontFamily={FontFamilyTypeEnum.OSWALD}
                gradient={photosTextColor}
                lineHeight="42px"
              >
                PHOTOS OF YOU
              </GradientText>
              <BasicText
                fontSize="18px"
                fontFamily={FontFamilyTypeEnum.POPPINS}
                fontWeight="600"
                color={COLORS.WHITE}
                lineHeight="25px"
              >
                During the action...
              </BasicText>
            </WelcomePageTextPart>
          )}

          {/* Always render sponsor image */}
          {sponsorImageUrl && (
            <BasicImgWrapper src={sponsorImageUrl} width="100%" height="auto" />
          )}

          <WelcomePageButton>
            <BasicButton
              width="100%"
              fontWeight="650"
              fontSize="18px"
              fontFamily={FontFamilyTypeEnum.SFPRO}
              color={buttonTextColor} // Use button text color from state
              onClick={handleNextPage}
              backgroundColor={buttonBackgroundColor}
              cursor="pointer"
              style={{ zIndex: 10, position: "relative", border: "none" }}
              margin="0 0 35px 0"
            >
              CHECK OUT YOUR PHOTOS
            </BasicButton>
          </WelcomePageButton>
          <WelcomePageOptOutButton>
            <a
              href="https://www.surveymonkey.com/r/momentooptout"
              style={{ textDecoration: "none" }}
            >
              <BasicButton
                width="100%"
                fontWeight="400"
                fontSize="9px"
                fontFamily={FontFamilyTypeEnum.SFPRO}
                padding="6px 20px 6px"
                color={COLORS.BLACK}
                style={{ backgroundColor: "#a7a4a4", border: "none" }}
                cursor="pointer"
                height="15px"
                lineHeight="1px"
              >
                Opt-Out of Momento
              </BasicButton>
            </a>
          </WelcomePageOptOutButton>
        </WelcomePageContent>
        <BasicText
          fontSize="6px"
          fontFamily={FontFamilyTypeEnum.POPPINS}
          fontWeight="150"
          color={COLORS.BLACK}
          lineHeight="8px"
          margin="0px 0px 7px 0px"
        >
          {VERSION_STRING}
        </BasicText>
        {
          showResumeSessionDialog && (
            <ResumeSessionModal
              onResume={handleResumePreviousSession}
              onNewSeat={handleSelectNewSeat}
              selectedSection={previousSession?.section}
              selectedRow={previousSession?.row}
              selectedSeat={previousSession?.seat}
              selectedSuite={previousSession?.suite}
            />
          )
        }
      </WelcomePageWrapper>
    </WelcomePageImageBackground>
  );
};

export default WelcomePage;
